/**
 * Project Pathways' durable graph records into the evidence compiler.
 *
 * The source check remains the authority for citation admission. This module
 * never admits a URL on its own: it only sees `Verification.sources`, which have
 * already crossed `reason.ts`'s returned-URL gate. A separate, explicit manual
 * path accepts only structured artefacts with stable origin identities and never
 * claims their URL was returned. Genealogy collapses both before counting.
 */

import type { CoverageTrace, RecordedSearchObservation } from './coverage'
import { coverageCertificate, recordedSearchObservation } from './coverage'
import type {
  ClaimType,
  CompilableClaim,
  CompilableEvidenceUnit,
  DisplayedEvidenceStatus,
  EpistemicCompileContext,
  EpistemicCompilation,
  EvidenceReach,
  EvidenceRuleProfile,
  EvidenceSourceClass,
  EvidenceStance,
  StudyDesign,
} from './epistemic'
import { compileEpistemics } from './epistemic'
import {
  corpusEvidenceRecordId,
  evidenceBindingId,
  outcomeEvidenceRecordId,
  webEvidenceRecordId,
} from './evidenceIds'
import type {
  EvidenceGenealogy,
  EvidenceOrigin,
  EvidenceRecord,
  GenealogyOverride,
} from './genealogy'
import { buildEvidenceGenealogy } from './genealogy'
import type {
  EvidenceAnnotation,
  ManualEvidenceBinding,
  ResearchAuditReceipt,
  ResearchClaimAnnotation,
  ResearchDecisionCriterionSignal,
  StoredCoverageTrace,
} from './research'
import { MANUAL_BINDABLE_EVIDENCE_ORIGINS, manualEvidenceBindingRejectionReasons } from './research'
import { pendingEvidenceStaleOf, verificationEvents } from './rebase'
import { liveOrigin, liveOriginUrl } from './sources'
import { taintOf } from './taint'
import type { ReasonNode, Session, SourceReading, Verification, WebSource } from './types'
import { outcomeLive, refutedIsEarned, verificationLive } from './types'
import { canonicalUrl, doiOf, isHttpUrl } from './url'

export interface EvidenceBinding {
  id: string
  nodeId: string
  verificationOpId: string | null
  verificationAt: number
  current: boolean
  recordId: string
  recordOrigin: EvidenceOrigin
  stance: EvidenceStance
  sourceClass: EvidenceSourceClass
  reach: EvidenceReach
  fullTextAvailable: boolean
  verifiedQuote: boolean
  studyDesign: StudyDesign
  populationMatch: boolean | null
  timeframeMatch: boolean | null
  admissionBasis: 'returned-citation' | 'manual-artifact'
  contentScope: 'full-text' | 'abstract-only' | 'metadata-only' | null
  citationReturned: boolean
  admitted: true
  rejectionReasons: []
}

export interface SessionEvidenceProjection {
  records: EvidenceRecord[]
  bindings: EvidenceBinding[]
  genealogy: EvidenceGenealogy
}

export interface CompileSessionResearchInput {
  session: Session
  profile: EvidenceRuleProfile
  overrides?: readonly GenealogyOverride[]
  extraRecords?: readonly EvidenceRecord[]
  annotations?: readonly EvidenceAnnotation[]
  manualEvidenceBindings?: readonly ManualEvidenceBinding[]
  claimAnnotations?: readonly ResearchClaimAnnotation[]
  decisionCriterionSignals?: readonly ResearchDecisionCriterionSignal[]
  auditReceipts?: readonly ResearchAuditReceipt[]
  coverageByOpId?: Readonly<Record<string, CoverageTrace>>
  /** Explicit structured facts may override mechanically derived run facts. */
  compileContext?: EpistemicCompileContext
  now?: number
}

export interface CompiledSessionResearch {
  evidence: SessionEvidenceProjection
  claims: CompilableClaim[]
  compilation: EpistemicCompilation
}

/** Recover the owning operation from check-audit metadata without guessing from display ids. */
export function coverageTracesByOpId(
  traces: readonly StoredCoverageTrace[],
): Record<string, StoredCoverageTrace> {
  const out: Record<string, StoredCoverageTrace> = {}
  for (const trace of traces) {
    let opId: string | undefined
    for (const round of trace.rounds) {
      for (const result of round.results) {
        const meta = result.meta
        if (!meta || typeof meta !== 'object' || Array.isArray(meta)) continue
        const candidate = meta.opId
        if (typeof candidate === 'string' && candidate.trim()) {
          opId = candidate
          break
        }
      }
      if (opId) break
    }
    if (!opId && trace.protocol.id.endsWith(':coverage')) {
      opId = trace.protocol.id.slice(0, -':coverage'.length)
    }
    out[opId ?? trace.protocol.id] = trace
  }
  return out
}

const scholarlyHosts = new Set([
  'arxiv.org',
  'biorxiv.org',
  'doi.org',
  'europepmc.org',
  'jamanetwork.com',
  'journals.plos.org',
  'medrxiv.org',
  'nature.com',
  'openalex.org',
  'pubmed.ncbi.nlm.nih.gov',
  'science.org',
  'sciencedirect.com',
  'springer.com',
  'thelancet.com',
  'wiley.com',
])

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return ''
  }
}

function domainEnds(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`)
}

function originOf(source: WebSource): EvidenceOrigin {
  const host = hostOf(source.url)
  return doiOf(source.url) || [...scholarlyHosts].some((row) => domainEnds(host, row))
    ? 'scholarly'
    : 'web'
}

/**
 * A conservative, named-by-code fallback for records that predate annotations.
 * It distinguishes only classes visible from transport metadata; study design,
 * population and timeframe remain unknown until a person or extractor supplies
 * them.
 */
export function inferredSourceClass(source: WebSource): EvidenceSourceClass {
  const host = hostOf(source.url)
  if (domainEnds(host, 'clinicaltrials.gov')) return 'registry'
  if (domainEnds(host, 'gov') || domainEnds(host, 'gov.uk') || domainEnds(host, 'europa.eu'))
    return 'government'
  if (domainEnds(host, 'iso.org') || domainEnds(host, 'ietf.org') || domainEnds(host, 'w3.org'))
    return 'standards'
  if (
    domainEnds(host, 'arxiv.org') ||
    domainEnds(host, 'biorxiv.org') ||
    domainEnds(host, 'medrxiv.org')
  )
    return 'preprint'
  // A DOI or journal host establishes a first-party scholarly record, not peer
  // review. Preprints, datasets and editorial records can all carry DOIs.
  if (doiOf(source.url) || [...scholarlyHosts].some((row) => domainEnds(host, row)))
    return 'web-primary'
  return 'web-secondary'
}

function reachOf(source: WebSource): EvidenceReach {
  if (source.textComplete === true) return 'full-text'
  if (source.quoteFound === true) return 'verified-quote'
  if (source.via === 'fetch') return 'metadata-only'
  return 'snippet'
}

function eventStance(reading: SourceReading): EvidenceStance {
  if (reading === 'supported') return 'supports'
  if (reading === 'refuted') return 'disputes'
  return 'context'
}

function annotationsById(rows: readonly EvidenceAnnotation[]): Map<string, EvidenceAnnotation> {
  return new Map(rows.map((row) => [`${row.key.kind}:${row.key.id}`, row]))
}

function annotatedBinding(
  base: EvidenceBinding,
  annotations: ReadonlyMap<string, EvidenceAnnotation>,
): EvidenceBinding {
  const record = annotations.get(`evidence-record:${base.recordId}`)
  const binding = annotations.get(`binding:${base.id}`)
  const value = { ...record, ...binding }
  return {
    ...base,
    sourceClass: value.sourceClass ?? base.sourceClass,
    studyDesign: value.studyDesign ?? base.studyDesign,
    populationMatch:
      value.populationMatch !== undefined ? value.populationMatch : base.populationMatch,
    timeframeMatch: value.timeframeMatch !== undefined ? value.timeframeMatch : base.timeframeMatch,
  }
}

function projectVerification(
  node: ReasonNode,
  verification: Verification,
  current: boolean,
  annotations: ReadonlyMap<string, EvidenceAnnotation>,
): { records: EvidenceRecord[]; bindings: EvidenceBinding[] } {
  const records: EvidenceRecord[] = []
  const bindings: EvidenceBinding[] = []
  for (const source of verification.sources) {
    const recordId = webEvidenceRecordId(source.url)
    const recordOrigin = originOf(source)
    records.push({
      id: recordId,
      origin: recordOrigin,
      label: source.title,
      url: source.url,
      ...(doiOf(source.url) ? { doi: doiOf(source.url) } : {}),
      ...(source.textHash ? { textHash: source.textHash } : {}),
      ...(source.simHash ? { simHash: source.simHash } : {}),
    })
    bindings.push(
      annotatedBinding(
        {
          id: evidenceBindingId(node.id, verification, recordId),
          nodeId: node.id,
          verificationOpId: verification.opId ?? null,
          verificationAt: verification.checkedAt,
          current,
          recordId,
          recordOrigin,
          stance: source.stance,
          sourceClass: inferredSourceClass(source),
          reach: reachOf(source),
          fullTextAvailable: source.textComplete === true,
          verifiedQuote: source.quoteFound === true,
          studyDesign: 'unknown',
          populationMatch: null,
          timeframeMatch: null,
          admissionBasis: 'returned-citation',
          contentScope: null,
          citationReturned: true,
          admitted: true,
          rejectionReasons: [],
        },
        annotations,
      ),
    )
  }
  for (const cite of verification.corpus?.cites ?? []) {
    const recordId = corpusEvidenceRecordId(cite.docId)
    const fetchedOrigin = liveOrigin(cite.docId)
    const url = liveOriginUrl(cite.docId)
    const sourceClass: EvidenceSourceClass = fetchedOrigin
      ? fetchedOrigin === 'clinicaltrials'
        ? 'registry'
        : 'web-primary'
      : 'internal-document'
    const recordOrigin: EvidenceOrigin = fetchedOrigin
      ? fetchedOrigin === 'clinicaltrials'
        ? 'registry'
        : 'scholarly'
      : 'corpus'
    records.push({
      id: recordId,
      origin: recordOrigin,
      label: cite.docTitle,
      ...(url ? { url } : {}),
      ...(url && doiOf(url) ? { doi: doiOf(url) } : {}),
    })
    bindings.push(
      annotatedBinding(
        {
          id: evidenceBindingId(node.id, verification, recordId),
          nodeId: node.id,
          verificationOpId: verification.opId ?? null,
          verificationAt: verification.checkedAt,
          current,
          recordId,
          recordOrigin,
          stance: eventStance(verification.reading),
          sourceClass,
          reach: 'verified-quote',
          fullTextAvailable: false,
          verifiedQuote: true,
          studyDesign: 'unknown',
          populationMatch: null,
          timeframeMatch: null,
          admissionBasis: 'returned-citation',
          contentScope: null,
          citationReturned: true,
          admitted: true,
          rejectionReasons: [],
        },
        annotations,
      ),
    )
  }
  return { records, bindings }
}

function uniqueRecords(rows: readonly EvidenceRecord[]): EvidenceRecord[] {
  const out = new Map<string, EvidenceRecord>()
  for (const row of rows) {
    const prior = out.get(row.id)
    if (!prior) {
      out.set(row.id, row)
      continue
    }
    // Prefer the copy carrying more mechanical identity without inventing a
    // merge rule here. Genealogy receives exactly one record per item id.
    out.set(row.id, {
      ...prior,
      ...row,
      trialIds: [...new Set([...(prior.trialIds ?? []), ...(row.trialIds ?? [])])],
      datasetIds: [...new Set([...(prior.datasetIds ?? []), ...(row.datasetIds ?? [])])],
      registryIds: [...new Set([...(prior.registryIds ?? []), ...(row.registryIds ?? [])])],
      runIds: [...new Set([...(prior.runIds ?? []), ...(row.runIds ?? [])])],
      cohortIds: [...new Set([...(prior.cohortIds ?? []), ...(row.cohortIds ?? [])])],
      datasetOverlapGroups: [
        ...new Set([...(prior.datasetOverlapGroups ?? []), ...(row.datasetOverlapGroups ?? [])]),
      ],
      registryOverlapGroups: [
        ...new Set([...(prior.registryOverlapGroups ?? []), ...(row.registryOverlapGroups ?? [])]),
      ],
      runOverlapGroups: [
        ...new Set([...(prior.runOverlapGroups ?? []), ...(row.runOverlapGroups ?? [])]),
      ],
      cohortOverlapGroups: [
        ...new Set([...(prior.cohortOverlapGroups ?? []), ...(row.cohortOverlapGroups ?? [])]),
      ],
      primaryReferences: [
        ...new Set([...(prior.primaryReferences ?? []), ...(row.primaryReferences ?? [])]),
      ],
      commonOrigins: [...new Set([...(prior.commonOrigins ?? []), ...(row.commonOrigins ?? [])])],
    })
  }
  return [...out.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

function manualSourceClass(origin: EvidenceOrigin): EvidenceSourceClass {
  if (origin === 'dataset') return 'dataset'
  if (origin === 'outcome') return 'recorded-outcome'
  return 'unknown'
}

function manualReach(scope: ManualEvidenceBinding['contentScope']): EvidenceReach {
  if (scope === 'full-text') return 'full-text'
  if (scope === 'abstract-only') return 'abstract-only'
  return 'metadata-only'
}

function annotatedManualBinding(
  base: EvidenceBinding,
  annotations: ReadonlyMap<string, EvidenceAnnotation>,
): EvidenceBinding {
  const record = annotations.get(`evidence-record:${base.recordId}`)
  const binding = annotations.get(`binding:${base.id}`)
  return {
    ...base,
    sourceClass: record?.sourceClass ?? base.sourceClass,
    studyDesign: record?.studyDesign ?? base.studyDesign,
    populationMatch:
      binding?.populationMatch !== undefined ? binding.populationMatch : base.populationMatch,
    timeframeMatch:
      binding?.timeframeMatch !== undefined ? binding.timeframeMatch : base.timeframeMatch,
  }
}

function projectManualBindings(
  session: Session,
  records: readonly EvidenceRecord[],
  rows: readonly ManualEvidenceBinding[],
  annotations: ReadonlyMap<string, EvidenceAnnotation>,
): EvidenceBinding[] {
  const recordsById = new Map(records.map((record) => [record.id, record]))
  return rows.flatMap((row) => {
    const record = recordsById.get(row.recordId)
    if (!session.nodes[row.nodeId]) return []
    if (manualEvidenceBindingRejectionReasons(row, record).length) return []
    if (
      !record ||
      !MANUAL_BINDABLE_EVIDENCE_ORIGINS.includes(
        record.origin as (typeof MANUAL_BINDABLE_EVIDENCE_ORIGINS)[number],
      )
    ) {
      return []
    }
    return [
      annotatedManualBinding(
        {
          id: row.id,
          nodeId: row.nodeId,
          verificationOpId: null,
          verificationAt: row.updatedAt,
          current: true,
          recordId: row.recordId,
          recordOrigin: record.origin,
          stance: row.stance,
          sourceClass: manualSourceClass(record.origin),
          reach: manualReach(row.contentScope),
          fullTextAvailable: row.contentScope === 'full-text',
          verifiedQuote: false,
          studyDesign: record.origin === 'cohort' ? 'cohort' : 'unknown',
          populationMatch: null,
          timeframeMatch: null,
          admissionBasis: 'manual-artifact',
          contentScope: row.contentScope,
          citationReturned: false,
          admitted: true,
          rejectionReasons: [],
        },
        annotations,
      ),
    ]
  })
}

export function projectSessionEvidence(
  session: Session,
  extraRecords: readonly EvidenceRecord[] = session.research?.evidenceRecords ?? [],
  overrides: readonly GenealogyOverride[] = session.research?.genealogyOverrides ?? [],
  annotations: readonly EvidenceAnnotation[] = session.research?.evidenceAnnotations ?? [],
  manualEvidenceBindings: readonly ManualEvidenceBinding[] = session.research
    ?.manualEvidenceBindings ?? [],
): SessionEvidenceProjection {
  const byId = annotationsById(annotations)
  const records: EvidenceRecord[] = [...extraRecords]
  const bindings: EvidenceBinding[] = []
  for (const nodeId of session.order) {
    const node = session.nodes[nodeId]
    if (!node) continue
    const events = verificationEvents(node)
    events.forEach((event, index) => {
      const projected = projectVerification(
        node,
        event,
        index === events.length - 1 && verificationLive(node),
        byId,
      )
      records.push(...projected.records)
      bindings.push(...projected.bindings)
    })
    // Deliberately NOT gated on `outcomeLive`, the same way every verification
    // event is pushed here and only the last one carries the liveness flag: this
    // is the ledger entry that an observation was recorded, which a later rewrite
    // does not undo. It reaches a claim's units only through a binding somebody
    // authored by hand, and that binding is their reading, not the compiler's.
    if (node.outcome) {
      records.push({
        id: outcomeEvidenceRecordId(node.id, node.outcome.recordedAt),
        origin: 'outcome',
        label: node.outcome.source || `Recorded outcome for ${node.label}`,
        runIds: [node.id],
      })
    }
  }
  const kept = uniqueRecords(records)
  bindings.push(...projectManualBindings(session, kept, manualEvidenceBindings, byId))
  return { records: kept, bindings, genealogy: buildEvidenceGenealogy(kept, overrides) }
}

function displayedStatus(node: ReasonNode): DisplayedEvidenceStatus {
  // The same liveness rule the check gets three lines down, and for the same
  // reason: a result observed against wording that has since been rewritten is
  // not a reading of the claim on the node now. `claimMarks` and `liveChannelOf`
  // already drop it, so the app never displays `observed-*` for one — reporting
  // it here would name a label the reader was never shown.
  if (node.outcome && outcomeLive(node)) return `observed-${node.outcome.bearing}`
  if (!node.verification || !verificationLive(node)) return 'unchecked'
  // Neither state represents admitted evidence. The compiler's vocabulary uses
  // `unchecked` for that mechanical fact; the source panel retains the more
  // specific reader-facing reason on the Verification itself.
  if (node.verification.reading === 'not-checkable' || node.verification.reading === 'unsourced')
    return 'unchecked'
  return node.verification.reading
}

/**
 * Claim class is authored research metadata, not a guess from a model-authored
 * node kind. The conservative legacy/default class makes every unannotated claim
 * compile under only the profile's `general` design rules.
 */
function claimTypeOf(
  nodeId: string,
  annotations: ReadonlyMap<string, ResearchClaimAnnotation>,
): ClaimType {
  return annotations.get(nodeId)?.claimType ?? 'general'
}

function evidenceUnits(
  nodeId: string,
  bindings: readonly EvidenceBinding[],
  genealogy: EvidenceGenealogy,
): CompilableEvidenceUnit[] {
  const familyByItem = new Map(
    genealogy.families.flatMap((family) => family.itemIds.map((id) => [id, family.id] as const)),
  )
  return bindings
    .filter((row) => row.nodeId === nodeId && row.current)
    .map((row) => ({
      id: row.id,
      familyId: familyByItem.get(row.recordId) ?? row.recordId,
      recordId: row.recordId,
      sourceClass: row.sourceClass,
      reach: row.reach,
      fullTextAvailable: row.fullTextAvailable,
      verifiedQuote: row.verifiedQuote,
      stance: row.stance,
      studyDesign: row.studyDesign,
      populationMatch: row.populationMatch,
      timeframeMatch: row.timeframeMatch,
      admissionBasis: row.admissionBasis,
      recordOrigin: row.recordOrigin,
      contentScope: row.contentScope,
      citationReturned: row.citationReturned,
      admitted: row.admitted,
      rejectionReasons: row.rejectionReasons,
    }))
}

function rejectedCitationUnits(
  verification: Verification | undefined,
  audits: readonly ResearchAuditReceipt[],
): CompilableEvidenceUnit[] {
  if (!verification?.opId) return []
  return audits
    .filter((audit) => audit.opId === verification.opId)
    .flatMap((audit) =>
      audit.citations
        .filter((citation) => citation.decision === 'rejected')
        .map((citation) => ({
          id: citation.id,
          familyId: isHttpUrl(citation.url) ? canonicalUrl(citation.url) : citation.id,
          recordId: citation.evidenceRecordId ?? null,
          sourceClass: 'unknown' as const,
          reach: 'unreadable' as const,
          fullTextAvailable: false,
          verifiedQuote: false,
          stance: 'context' as const,
          studyDesign: 'unknown' as const,
          populationMatch: null,
          timeframeMatch: null,
          admissionBasis: 'rejected-citation' as const,
          recordOrigin: null,
          contentScope: null,
          citationReturned: citation.rawResultId !== null,
          admitted: false,
          rejectionReasons: [citation.reason],
        })),
    )
}

function coverageFor(
  verification: Verification | undefined,
  traces: Readonly<Record<string, CoverageTrace>>,
): {
  partial: boolean
  families: string[]
  sources: string[]
  interval: { lower: number; upper: number } | null
  observation: RecordedSearchObservation | null
} {
  if (!verification?.opId || !traces[verification.opId]) {
    return {
      partial: verification?.partial === true,
      families: [],
      sources: [],
      interval: null,
      observation: null,
    }
  }
  const certificate = coverageCertificate(traces[verification.opId]!)
  const incompleteKinds = new Set([
    'no-search',
    'unsearched-family',
    'unsearched-source',
    'unsearched-combination',
    'failed-round',
    'partial-round',
    'unknown-result-count',
    'unrecorded-results',
  ])
  return {
    partial:
      verification.partial === true ||
      certificate.rounds.some((round) => round.completion.kind !== 'complete') ||
      certificate.diagnostics.some((diagnostic) => incompleteKinds.has(diagnostic.kind)),
    families: [...new Set(certificate.rounds.map((round) => round.query.familyId))],
    sources: [...new Set(certificate.rounds.map((round) => round.retrievalSourceId))],
    interval: { lower: certificate.interval.lower, upper: certificate.interval.upper },
    observation: recordedSearchObservation(certificate),
  }
}

export function compilableSessionClaims(
  session: Session,
  evidence: SessionEvidenceProjection,
  coverageByOpId: Readonly<Record<string, CoverageTrace>> = {},
  now = Date.now(),
  auditReceipts: readonly ResearchAuditReceipt[] = [],
  claimAnnotations: readonly ResearchClaimAnnotation[] = [],
  recordedSearch = true,
): CompilableClaim[] {
  const claimTypes = new Map(claimAnnotations.map((annotation) => [annotation.nodeId, annotation]))
  return session.order.flatMap((nodeId) => {
    const node = session.nodes[nodeId]
    if (!node) return []
    const current = node.verification && verificationLive(node) ? node.verification : undefined
    const units = [
      ...evidenceUnits(node.id, evidence.bindings, evidence.genealogy),
      ...rejectedCitationUnits(current, auditReceipts),
    ]
    const uniqueFamilies = new Set(units.map((unit) => unit.familyId)).size
    const coverage = coverageFor(current, coverageByOpId)
    const stale = pendingEvidenceStaleOf(session, node.id)
    const taint = taintOf(session, node.id)
    const events = verificationEvents(node)
    const previous = events.at(-2)
    return [
      {
        nodeId: node.id,
        claim: node.claim,
        claimType: claimTypeOf(node.id, claimTypes),
        displayedStatus: displayedStatus(node),
        evidence: units,
        checkedAt: current?.checkedAt ?? null,
        now,
        partialSearch: coverage.partial,
        searchFailures: current?.failures ?? [],
        queryFamilies: coverage.families,
        retrievalSources: coverage.sources,
        estimatedCoverage: coverage.interval,
        ...(recordedSearch ? { searchObservation: coverage.observation } : {}),
        taintedBy: taint ? [taint.sourceId] : [],
        staleBecause: stale?.changes.map((change) => change.sourceId) ?? [],
        modelProvenanceChanged:
          !!previous &&
          !!current &&
          (previous.model.provider !== current.model.provider ||
            previous.model.id !== current.model.id),
        duplicateEvidenceCount: Math.max(0, units.length - uniqueFamilies),
        coreRefutation: {
          rule: 'core.refuted-is-earned',
          passed: current ? refutedIsEarned(current.sources) : false,
          sources: (current?.sources ?? []).map((source) => ({
            url: source.url,
            stance: source.stance,
            via: source.via,
            quoteFound: source.quoteFound ?? null,
            textHash: source.textHash ?? null,
            simHash: source.simHash ?? null,
          })),
        },
        // Gated for the reason above, and this half is the one that spends: an
        // ungated bearing short-circuits `compileEpistemics` to
        // `outcome.outranks-evidence`, and because `displayedStatus` read the
        // same stale record the two agreed and no `unearned-label` fired.
        ...(node.outcome && outcomeLive(node) ? { recordedOutcome: node.outcome.bearing } : {}),
      },
    ]
  })
}

export function compileSessionResearch(
  input: CompileSessionResearchInput,
): CompiledSessionResearch {
  const now = input.now ?? Date.now()
  const evidence = projectSessionEvidence(
    input.session,
    input.extraRecords,
    input.overrides,
    input.annotations,
    input.manualEvidenceBindings,
  )
  const claims = compilableSessionClaims(
    input.session,
    evidence,
    input.coverageByOpId,
    now,
    input.auditReceipts ?? input.session.research?.auditReceipts,
    input.claimAnnotations ?? input.session.research?.claimAnnotations,
    input.profile.search.policy === 'recorded-search-v1',
  )
  const correctionSourcesByNode: Record<string, string[]> = {}
  const retractionSourcesByNode: Record<string, string[]> = {}
  const declaredCriteria = new Set(
    (input.session.brief ?? [])
      .filter((directive) => directive.kind === 'criterion')
      .map((directive) => directive.text),
  )
  const decisionSignals = (
    input.decisionCriterionSignals ??
    input.session.research?.decisionCriterionSignals ??
    []
  ).filter((signal) => declaredCriteria.has(signal.criterion))
  for (const claim of claims) {
    const stale = pendingEvidenceStaleOf(input.session, claim.nodeId)
    for (const change of stale?.changes ?? []) {
      const source = input.session.nodes[change.sourceId]
      const notices = source?.verification?.corpus?.cites.flatMap((cite) =>
        cite.notice ? [cite.notice] : [],
      )
      if (notices?.includes('flagged')) {
        const rows = correctionSourcesByNode[claim.nodeId]
        if (rows) rows.push(change.sourceId)
        else correctionSourcesByNode[claim.nodeId] = [change.sourceId]
      }
      if (notices?.includes('retracted')) {
        const rows = retractionSourcesByNode[claim.nodeId]
        if (rows) rows.push(change.sourceId)
        else retractionSourcesByNode[claim.nodeId] = [change.sourceId]
      }
    }
  }
  const compileContext: EpistemicCompileContext = {
    correctionSourcesByNode,
    retractionSourcesByNode,
    ...(decisionSignals.length
      ? {
          decisionCriterionMet: decisionSignals.some((signal) => signal.status === 'met'),
        }
      : {}),
    ...input.compileContext,
  }
  return {
    evidence,
    claims,
    compilation: compileEpistemics(input.profile, claims, now, compileContext),
  }
}
